import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  List,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  isApiError,
  mergeBookingCancellationResponse,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import type {
  BookingCancelRequest,
  BookingResponse,
} from "@bw/shared/types/generated";
import { groupBookingSets } from "@bw/shared/utils";
import { MaterialBookingSetCard } from "../../../common/components/MaterialBookingSetCard";
import { useAuth } from "@bw/shared/hooks";
import { Page } from "../../../common/components/Page";
import { useMyBookings } from "../hooks/useMyBookings";
import { useCancellationQuote } from "../hooks/useCancellationQuote";
import { useCancelBooking } from "../hooks/useCancelBooking";
import { BookingHistoryRow } from "../components/BookingHistoryRow";
import { CancelBookingDialog } from "../components/CancelBookingDialog";

/**
 * The player's bookings at a facility (slice 3c). Route /book/:facilityId/bookings.
 *
 * Lists reservations the token user hosts or currently participates in, ordered
 * by server start time. Host rows may open cancellation; participant rows remain
 * viewable with redacted money and no host-only action. Opening the dialog
 * requests a short-lived, server-issued quote; confirming commits that exact
 * quote with an idempotency key.
 *
 * This route is facility-scoped; `/me/bookings` provides the cross-facility view.
 */
export function MyBookingsPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { user } = useAuth();
  const viewerUserId = user?.uid;

  const bookingsQ = useMyBookings(facilityId);
  const cancel = useCancelBooking(facilityId);

  // The booking whose cancel dialog is open (null = closed).
  const [target, setTarget] = useState<BookingResponse | null>(null);
  const [quoteRequestVersion, setQuoteRequestVersion] = useState(0);
  const [decisionChanged, setDecisionChanged] = useState(false);
  const [cancellationNotice, setCancellationNotice] = useState("");
  const cancellationNoticeRef = useRef<HTMLParagraphElement>(null);
  const cancellationRequestRef = useRef<BookingCancelRequest | null>(null);
  // Server responses for bookings just cancelled this session — they carry the
  // authoritative refund_amount + cancelled_at + status, which we overlay onto
  // the list so the row reflects the real outcome (not the preview).
  const [cancelled, setCancelled] = useState<Record<string, BookingResponse>>(
    {},
  );

  const quoteQ = useCancellationQuote(
    target?.booking_id,
    quoteRequestVersion,
    Boolean(target),
  );

  const rows = useMemo(() => {
    const list = bookingsQ.data ?? [];
    return [...list]
      .map((b) => cancelled[b.booking_id] ?? b)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [bookingsQ.data, cancelled]);

  // Group by SET (recurring_group_id) so a multi-court / recurring reservation
  // reads as ONE summary card — consistent with the cross-facility calendar and
  // the "invite the whole set" behaviour. A single booking stays one row.
  const sets = useMemo(() => groupBookingSets(rows), [rows]);

  useEffect(() => {
    if (!target) return;
    const canonical = rows.find(
      (booking) => booking.booking_id === target.booking_id,
    );
    if (
      canonical?.status === "confirmed" &&
      canonical.cancellation_recovery_pending !== true
    ) {
      setTarget(canonical);
      return;
    }
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    cancel.reset();
    setTarget(null);
    setCancellationNotice(
      canonical
        ? "The booking status changed. Cancellation controls closed."
        : "The booking is no longer available. Cancellation controls closed.",
    );
  }, [cancel, rows, target?.booking_id]);

  useEffect(() => {
    if (cancellationNotice) cancellationNoticeRef.current?.focus();
  }, [cancellationNotice]);

  function handleConfirmCancel() {
    if (!target || !quoteQ.data) return;
    const request = cancellationRequestRef.current ?? {
      quote_id: quoteQ.data.quote_id,
      idempotency_key: crypto.randomUUID(),
    };
    cancellationRequestRef.current = request;
    setDecisionChanged(false);
    cancel.mutate(
      {
        bookingId: target.booking_id,
        request,
      },
      {
        onSuccess: (res) => {
          setCancelled((prev) => ({
            ...prev,
            [res.booking_id]: mergeBookingCancellationResponse(target, res),
          }));
          setTarget(null);
        },
        onError: async (error) => {
          if (isApiError(error) && error.details?.refresh_required === true) {
            cancellationRequestRef.current = null;
            const refreshed = await bookingsQ.refetch();
            if (refreshed.isError) {
              setTarget(null);
              setCancellationNotice(
                "The current booking status could not be confirmed. Cancellation controls closed.",
              );
              return;
            }
            const canonical = refreshed.data?.find(
              (booking) => booking.booking_id === target.booking_id,
            );
            if (
              canonical?.status === "confirmed" &&
              canonical.cancellation_recovery_pending !== true
            ) {
              setTarget(canonical);
              setDecisionChanged(true);
              setQuoteRequestVersion((version) => version + 1);
            } else {
              cancel.reset();
              setDecisionChanged(false);
              setTarget(null);
              setCancellationNotice(
                canonical
                  ? "The booking status changed. Cancellation controls closed."
                  : "The booking is no longer available. Cancellation controls closed.",
              );
            }
          }
        },
      },
    );
  }

  function openDialog(booking: BookingResponse) {
    cancel.reset();
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setCancellationNotice("");
    setQuoteRequestVersion((version) => version + 1);
    setTarget(booking);
  }

  function closeDialog() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setTarget(null);
  }

  function refreshQuote() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    cancel.reset();
    setDecisionChanged(false);
    setQuoteRequestVersion((version) => version + 1);
  }

  // ---- List states ----------------------------------------------------------
  let body: React.ReactNode;
  if (bookingsQ.isLoading) {
    body = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress size={22} />
        <Typography color="text.secondary">Loading your bookings…</Typography>
      </Stack>
    );
  } else if (bookingsQ.isError) {
    body = (
      <Alert severity="error" role="alert">
        Something went wrong loading your bookings. Please try again.
      </Alert>
    );
  } else if (rows.length === 0) {
    body = (
      <Alert severity="info" role="status">
        No upcoming bookings yet. Reserve a court to see it here.
      </Alert>
    );
  } else {
    body = (
      <Paper component="section" variant="outlined" aria-label="Your bookings">
        <List disablePadding>
          {sets.map((set) =>
            set.isGroup ? (
              // A multi-court / recurring reservation → ONE summary card (single
              // total + a single "View" into the set detail; NO per-set Cancel —
              // cancellation is per-court on the detail page).
              <MaterialBookingSetCard key={set.key} set={set} />
            ) : (
              // A single booking → its existing row (View + Cancel), unchanged.
              // The court name lives on the row so each booking is identified.
              <BookingHistoryRow
                key={set.anchor.booking_id}
                booking={set.anchor}
                courtName={set.anchor.court_name?.trim() || "Court"}
                isHost={
                  Boolean(viewerUserId) && set.anchor.user_id === viewerUserId
                }
                onCancel={openDialog}
              />
            ),
          )}
        </List>
      </Paper>
    );
  }

  return (
    <Page title="My bookings">
      <Stack spacing={2}>
        {cancellationNotice ? (
          <Box
            component="p"
            ref={cancellationNoticeRef}
            role="status"
            aria-live="polite"
            tabIndex={-1}
            sx={{ m: 0 }}
          >
            {cancellationNotice}
          </Box>
        ) : null}
        {body}
      </Stack>

      {target ? (
        <CancelBookingDialog
          booking={target}
          quote={quoteQ.data}
          quoteLoading={quoteQ.isFetching}
          quoteError={quoteQ.error}
          onQuoteRetry={refreshQuote}
          onConfirm={handleConfirmCancel}
          onClose={closeDialog}
          cancelPending={cancel.isPending}
          cancelError={cancel.error}
          decisionChanged={decisionChanged}
        />
      ) : null}
    </Page>
  );
}
